import React from "react";
import { VideoHistoryList } from "@/components/reelflo/VideoHistoryList";

// Tray membership — THE safety predicate. A card stays in the tray until the
// server proves it reached Finished Videos AND the user has acknowledged the
// completion. It leaves ONLY when the video is delivered
// (ready_in_finished_videos === true) AND the one-time completion flag has been
// cleared (new_completion !== true). Anything else — processing, failed,
// done-but-not-handed-off, delivered-but-unacknowledged, and even an
// unknown/malformed status — keeps the card visible. Historical documents with
// a missing new_completion default to false, so an already-delivered old video
// stays HIDDEN (it never floods the tray).
//
// One display-only age rule sits on top for FAILED videos: a failure stays
// visible for 48 hours (so the customer sees what happened and can re-upload),
// then is hidden from this Dashboard tray. The record itself is never touched —
// GET /videos keeps returning it. A failed video with a missing/blank/invalid
// or future created_at is NEVER hidden by guessing.
export const FAILED_VISIBILITY_WINDOW_MS = 48 * 60 * 60 * 1000;

// A second display-only age rule for STALE non-failed cards (Processing /
// Finalizing / Needs Attention): after 7 days they are hidden from this
// Dashboard tray exactly like aged failures. Same fail-open parsing — a
// missing/blank/invalid or future created_at is NEVER hidden by guessing.
// Ready/delivered cards are never aged out. The record itself is never
// touched — GET /videos keeps returning it and the Videos page still shows it.
export const STALE_VISIBILITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export const isInProcessingTray = (video, nowMs = Date.now()) => {
    const eligible =
        video?.ready_in_finished_videos !== true || video?.new_completion === true;
    if (!eligible) return false;
    if (video?.status !== "failed") {
        // Delivered-but-unacknowledged (Ready) cards are never aged out.
        if (video?.ready_in_finished_videos === true) return true;
        const rawStale = video?.created_at;
        if (typeof rawStale !== "string" || rawStale.trim() === "") return true;
        const staleCreatedAtMs = Date.parse(rawStale);
        if (!Number.isFinite(staleCreatedAtMs)) return true;
        return nowMs - staleCreatedAtMs <= STALE_VISIBILITY_WINDOW_MS;
    }
    const raw = video?.created_at;
    if (typeof raw !== "string" || raw.trim() === "") return true;
    const createdAtMs = Date.parse(raw);
    if (!Number.isFinite(createdAtMs)) return true;
    const ageMs = nowMs - createdAtMs;
    return ageMs <= FAILED_VISIBILITY_WINDOW_MS;
};

// Temporary, secondary processing tray for the Dashboard. Renders NOTHING when
// no video satisfies the predicate (no empty box, heading, or wrapper height).
// Reuses VideoHistoryList (JourneyNode + STAGES + the failed-card action + the
// finished-video handoff CTA) via its tray mode, which also suppresses Dashboard
// download controls.
export const ProcessingTray = ({
    videos,
    celebrationId,
    onUploadAgain,
    onViewFinished,
    ackingId,
    containerRef,
}) => {
    // Explicit arrow so Array.filter's index argument can never impersonate
    // the predicate's optional nowMs clock parameter.
    const trayVideos = (videos || []).filter((video) => isInProcessingTray(video));
    if (trayVideos.length === 0) return null;
    return (
        <div
            ref={containerRef}
            data-testid="processing-tray"
            className="mt-10 scroll-mt-24"
        >
            <div className="mb-3 flex items-center justify-between">
                <h2
                    data-testid="processing-tray-title"
                    className="rf-heading text-lg font-semibold text-white/80"
                >
                    In Progress
                </h2>
                <span className="text-sm font-medium text-white/40">
                    {trayVideos.length} item{trayVideos.length === 1 ? "" : "s"}
                </span>
            </div>
            <VideoHistoryList
                trayMode
                videos={trayVideos}
                celebrationId={celebrationId}
                onUploadAgain={onUploadAgain}
                onViewFinished={onViewFinished}
                ackingId={ackingId}
            />
        </div>
    );
};
